import { useEffect, useRef } from 'react';

// Module-scoped counter so nested modals don't stomp on each other's
// body-scroll-lock restore. Every open() increments; every close()
// decrements. Only the first open locks, only the last close
// releases — intermediate layers are transparent.
let openModalCount = 0;
let savedOverflow = '';

/**
 * useModalA11y — one hook that gives any modal / overlay the table-
 * stakes accessibility behaviour WCAG 2.2 expects:
 *
 *   • Escape key closes the modal.
 *   • Keyboard focus is trapped inside the modal while open (Tab /
 *     Shift+Tab cycles through interactive elements).
 *   • When the modal opens, focus moves to the first focusable
 *     element inside it (or the container itself if none exist).
 *   • When the modal closes, focus returns to the element that had
 *     it immediately before the modal opened — so a user who opened
 *     the modal from a "Share" button lands back on that button,
 *     not at the top of the page.
 *   • Background scroll is locked while open (so the page behind
 *     doesn't slide under the backdrop on iOS Safari).
 *
 * Usage:
 *
 *   const containerRef = useModalA11y({ open, onClose });
 *   return <div ref={containerRef} role="dialog" aria-modal="true">...</div>;
 */
export function useModalA11y({ open, onClose, returnFocus = true } = {}) {
  const containerRef = useRef(null);
  const returnFocusRef = useRef(null);

  useEffect(() => {
    if (!open) return;

    // Remember who had focus before the modal opened so we can
    // return focus on close. Defensive: the element might be null
    // in headless environments.
    if (returnFocus) {
      const active = typeof document !== 'undefined' ? document.activeElement : null;
      returnFocusRef.current = (active && active instanceof HTMLElement) ? active : null;
    }

    const container = containerRef.current;
    if (!container) return;

    // Focus the first focusable element inside the modal, or the
    // container itself if none exist. Tabindex -1 on the container
    // makes it focusable without joining the Tab order.
    const focusable = getFocusable(container);
    const first = focusable[0] || container;
    if (!container.hasAttribute('tabindex') && first === container) {
      container.setAttribute('tabindex', '-1');
    }
    // Delay one frame so the browser has committed layout — focusing
    // before the element is on screen can silently fail.
    const raf = requestAnimationFrame(() => {
      try { first.focus({ preventScroll: true }); } catch {}
    });

    // Scroll-lock, reference-counted so nested modals don't fight
    // over body.style.overflow. The first open saves the original
    // value and locks; subsequent opens just increment the counter;
    // the last close restores the saved value. A mid-stack close
    // can't accidentally release the lock for a still-open outer
    // modal, and a close on an already-empty stack is a safe no-op.
    if (openModalCount === 0) {
      savedOverflow = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
    }
    openModalCount++;

    function onKey(e) {
      if (e.key === 'Escape') {
        e.stopPropagation();
        onClose?.();
        return;
      }
      if (e.key !== 'Tab') return;
      // Focus trap — keep Tab cycling inside the modal.
      const list = getFocusable(container);
      if (!list.length) {
        e.preventDefault();
        return;
      }
      const firstEl = list[0];
      const lastEl = list[list.length - 1];
      const active = document.activeElement;
      if (e.shiftKey && active === firstEl) {
        e.preventDefault();
        lastEl.focus();
      } else if (!e.shiftKey && active === lastEl) {
        e.preventDefault();
        firstEl.focus();
      }
    }
    window.addEventListener('keydown', onKey, true);

    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener('keydown', onKey, true);
      openModalCount = Math.max(0, openModalCount - 1);
      if (openModalCount === 0) {
        document.body.style.overflow = savedOverflow;
      }
      // Return focus to the element that originally triggered the
      // modal. If that element isn't in the DOM anymore, fall back
      // to document.body.
      if (returnFocus && returnFocusRef.current) {
        try { returnFocusRef.current.focus({ preventScroll: true }); } catch {}
      }
    };
  }, [open, onClose, returnFocus]);

  return containerRef;
}

// ── helpers ──────────────────────────────────────────────────────

const FOCUSABLE_SELECTOR = [
  'a[href]',
  'button:not([disabled])',
  'input:not([disabled]):not([type="hidden"])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
  'audio[controls]',
  'video[controls]',
].join(',');

function getFocusable(root) {
  if (!root) return [];
  return Array.from(root.querySelectorAll(FOCUSABLE_SELECTOR))
    .filter(el => !el.hasAttribute('aria-hidden') && el.offsetParent !== null);
}
